// API数据获取封装 - Composables方式
interface ApiResponse<T> {
  data: T
  status: number
  message?: string
}

interface ApiError {
  status: number
  message: string
}

interface ApiOptions extends RequestInit {
  baseURL?: string
  timeout?: number
}

// 基础API客户端类
class ApiClient {
  private baseURL: string = ''
  private defaultOptions: ApiOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 20000
  }

  constructor(baseURL?: string) {
    this.baseURL = baseURL || ''
  }

  private async request<T>(url: string, options: ApiOptions = {}): Promise<ApiResponse<T>> {
    const config: ApiOptions = {
      ...this.defaultOptions,
      ...options,
      headers: {
        ...this.defaultOptions.headers,
        ...options.headers,
      }
    }

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), config.timeout)
      
      const response = await fetch(`${this.baseURL}${url}`, {
        ...config,
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)

      const data = await response.json()

      if (!response.ok) {
        throw {
          status: response.status,
          message: data.message || '请求失败'
        } as ApiError
      }

      return {
        data,
        status: response.status
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        throw {
          status: 408,
          message: '请求超时'
        } as ApiError
      }
      throw {
        status: 500,
        message: (error as Error).message || '网络错误'
      } as ApiError
    }
  }

  async get<T>(url: string, options?: ApiOptions): Promise<ApiResponse<T>> {
    return this.request<T>(url, { method: 'GET', ...options })
  }

  async post<T>(url: string, data?: any, options?: ApiOptions): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...options
    })
  }

  async put<T>(url: string, data?: any, options?: ApiOptions): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options
    })
  }

  async delete<T>(url: string, options?: ApiOptions): Promise<ApiResponse<T>> {
    return this.request<T>(url, { method: 'DELETE', ...options })
  }
}

// Composables方式API封装
export const useApi = () => {
  const config = useRuntimeConfig()
  const baseURL = config.public.apiBase as string
  
  // 创建API客户端实例
  const client = new ApiClient(baseURL)

  // 响应式数据
  const loading = ref(false)
  const error = ref<ApiError | null>(null)

  // 清除错误
  const clearError = () => {
    error.value = null
  }

  // 带状态管理的请求方法
  const requestWithState = async <T>(
    requestFn: () => Promise<ApiResponse<T>>
  ): Promise<ApiResponse<T> | null> => {
    loading.value = true
    error.value = null

    try {
      const response = await requestFn()
      return response
    } catch (err) {
      error.value = err as ApiError
      return null
    } finally {
      loading.value = false
    }
  }

  // 组合式API方法
  const get = async <T>(url: string, options?: ApiOptions) => {
    return requestWithState<T>(() => client.get<T>(url, options))
  }

  const post = async <T>(url: string, data?: any, options?: ApiOptions) => {
    return requestWithState<T>(() => client.post<T>(url, data, options))
  }

  const put = async <T>(url: string, data?: any, options?: ApiOptions) => {
    return requestWithState<T>(() => client.put<T>(url, data, options))
  }

  const del = async <T>(url: string, options?: ApiOptions) => {
    return requestWithState<T>(() => client.delete<T>(url, options))
  }

  return {
    // 状态
    loading: readonly(loading),
    error: readonly(error),
    
    // 方法
    clearError,
    get,
    post,
    put,
    delete: del,
    
    // 原始客户端（用于不需要状态管理的请求）
    client
  }
}

// 基于useFetch的Composables方式
export const useFetchData = <T>(url: string, options?: any) => {
  const config = useRuntimeConfig()
  const baseURL = config.public.apiBase as string
  
  return useFetch<T>(url, {
    baseURL,
    ...options
  })
}

// 特定API的Composables
export const useHttpApi = () => {
  const { get, post, loading, error } = useApi()

  // 获取公司简介
  const getCompanyProfileApi = async () => {
    return get<{ data: any }>('/company-profile/get')
  }

  // 获取产品列表
  const getProductListApi = async (params: any) => {
    console.log("params",params.productPlatform,params.product)
    return get<{ data: any }>(`/company-product/list?productPlatform=${params.productPlatform}&product=${params.product}`)
  }

  // 获取产品详情
  const getProductDetailApi = async (id: string) => {
    return get<{ data: any }>(`/company-product/get/${id}`)
  }

  // 获取新闻类别
  const getNewsCategoryApi = async () => {
    return get<{ data: any }>('/company-news/getVailTypeList')
  }

  // 获取首页新闻
  const getHomeNewsApi = async () => {
    return get<{ data: any }>('/company-news/getIndexMapList')
  }

  // 获取新闻列表
  const getNewsListApi = async (options: any) => {
    return get<{ data: any }>(`/company-news/page?type=${options.type}&pageNo=${options.pageNo}&pageSize=${options.pageSize}`)
  }

  // 获取新闻详情
  const getNewsDetailApi = async (id: string) => {
    return get<{ data: any }>(`/company-news/get?id=${id}`)
  }

  // 获取案例列表
  const getCaseListApi = async (isHomeShow?: boolean) => {
    return get<{ data: any }>(`/company-case/list?isHomeShow=${isHomeShow}`)
  }

  // 获取案例详情
  const getCaseDetailApi = async (id: string) => {
    return get<{ data: any }>(`/company-case/get?id=${id}`)
  }

  // 获取联系方式
  const getContactApi = async () => {
    return get<{ data: any }>('/company-contact-info/get')
  }

  // 获取合作与服务
  const getCooperationApi = async () => {
    return get<{ data: any }>('/cooperation-serve/list')
  }

  // 获取公司联系方式
  const getCompanyContactApi = async () => {
    return get<{ data: any }>('/company-contact-info/get')
  }

  // 获取数据字典
  const getDictApi = async (type: string) => {
    return get<{ data: any }>(`/system/dict-data/getDictDataByType?dictType=${type}`)
  }

  // 发送手机验证码
  const sendSmsCodeApi = async (phone: string) => {
    return get<{ data: any }>(`/company-customer/send-sms-code?mobile=${phone}`)
  }

  // 提交洽谈
  const submitTalkApi = async (data: any) => {
    return post<{ data: any }>('/company-customer/create', data)
  }

  // 获取荣誉列表
  const getHonorListApi = async () => {
    return get<{ data: any }>('/company-honor/list')
  }

  // 获取banner列表
  const getBannerListApi = async (type: string) => {
    return get<{ data: any }>(`/promotional_space/list?pageType=${type}`)
  }

  // 获取平台介绍
  const getPlatformIntroductionApi = async () => {
    return get<{ data: any }>('/platform-introduction/get')
  }

  const fetchTeamDetail = async (id: string) => {
    return get<{ data: any }>(`/api/team/${id}`)
  }

  const createTeam = async (data: any) => {
    return post<{ id: string }>('/api/team/create', data)
  }

  return {
    loading,
    error,
    getCompanyProfileApi,
    getProductListApi,
    getProductDetailApi,
    getHomeNewsApi,
    getNewsCategoryApi,
    getNewsListApi,
    getNewsDetailApi,
    getCaseListApi,
    getCaseDetailApi,
    getContactApi,
    getCooperationApi,
    getCompanyContactApi,
    getDictApi,
    sendSmsCodeApi,
    getHonorListApi,
    submitTalkApi,
    getBannerListApi,
    getPlatformIntroductionApi,
    fetchTeamDetail,
    createTeam
  }
}